import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  Target, Star, Trophy, ChevronLeft, Save, RefreshCw, Users, Globe,
  MessageSquare, Palette, CheckCircle2, Quote, Plus, Trash2,
  Eye, EyeOff, Calendar, ChevronRight, Sparkles, Zap, Layers,
} from 'lucide-react';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import {
  getSalesGoals, getSalespeople, setSalesGoal, deleteSalesGoal,
  getSalesQuotes, addSalesQuote, deleteSalesQuote, toggleSalesQuote,
  getGoalMonitor,
} from '../../lib/api';
import type { SalesGoalData, SalesPersonInfo, SalesQuoteData, TierThresholds, GoalMonitorEntry, GoalMonitorWeek } from '../../lib/api';

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function mondayOf(d: Date): Date {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const m = new Date(d);
  m.setDate(m.getDate() + diff);
  m.setHours(0,0,0,0);
  return m;
}
// The Monday's own date (same as the app) — toISOString would turn India's
// midnight into the Sunday before
function weekKey(d: Date) {
  const m = mondayOf(d);
  return `${m.getFullYear()}-${String(m.getMonth()+1).padStart(2,'0')}-${String(m.getDate()).padStart(2,'0')}`;
}
function weekLabel(d: Date) {
  const mon = mondayOf(d);
  const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
  const fmt = (x: Date) => x.toLocaleDateString('en-IN',{day:'numeric',month:'short'});
  return `${fmt(mon)} – ${fmt(sun)}`;
}
function labelForKey(key: string) {
  const [y, m, d] = key.split('-').map(Number);
  return weekLabel(new Date(y, m - 1, d));
}
function weekOptions() {
  const weeks = [];
  for (let i = -1; i <= 3; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i * 7);
    weeks.push({ key: weekKey(d), label: weekLabel(d), isNow: i === 0 });
  }
  return weeks;
}

/* ── Preset data ──────────────────────────────────────────────────────────── */
const THEMES = [
  { label:'Purple Fire',  color:'#6d28d9', grad:'linear-gradient(135deg,#6d28d9,#a855f7,#ec4899)' },
  { label:'Golden Blaze', color:'#d97706', grad:'linear-gradient(135deg,#f59e0b,#ef4444,#ec4899)' },
  { label:'Ocean Surge',  color:'#0ea5e9', grad:'linear-gradient(135deg,#0ea5e9,#6366f1,#8b5cf6)' },
  { label:'Emerald Win',  color:'#16a34a', grad:'linear-gradient(135deg,#16a34a,#0ea5e9,#6366f1)' },
  { label:'Crimson Surge',color:'#dc2626', grad:'linear-gradient(135deg,#dc2626,#f97316,#fbbf24)' },
  { label:'Night Pro',    color:'#1e1b4b', grad:'linear-gradient(135deg,#1e1b4b,#312e81,#4f46e5)' },
];
const EMOJIS = ['🎯','🔥','⚡','🏆','💎','🚀','🌟','💪','🎪','🦁','👑','🥇'];
const BADGE_PRESETS = [
  { label:'⭐ Star of the Week',   color:'#f59e0b', bg:'#fef9c3' },
  { label:'🏆 Top Closer',         color:'#d97706', bg:'#fef3c7' },
  { label:'💎 Diamond Performer',  color:'#0ea5e9', bg:'#e0f2fe' },
  { label:'🔥 On Fire',            color:'#ef4444', bg:'#fee2e2' },
  { label:'🚀 Rocket Achiever',    color:'#7c3aed', bg:'#ede9fe' },
  { label:'👑 Sales King',         color:'#9333ea', bg:'#f3e8ff' },
  { label:'💪 Hustle King',        color:'#16a34a', bg:'#dcfce7' },
  { label:'⚡ Lightning Closer',   color:'#f59e0b', bg:'#fff7ed' },
  { label:'🌟 Rising Star',        color:'#0284c7', bg:'#e0f2fe' },
  { label:'🎯 Goal Crusher',       color:'#dc2626', bg:'#fef2f2' },
  { label:'🦁 Lion Heart',         color:'#b45309', bg:'#fef3c7' },
  { label:'🥇 Gold Standard',      color:'#ca8a04', bg:'#fefce8' },
];
const HEADLINE_PRESETS = [
  'Crush Your Goals This Week! 💪',
  'Every Call Counts. Every Close Matters.',
  'Champions are Made When No One is Watching 🏆',
  'Your Effort Today is Tomorrow\'s Success Story 🌟',
  'Be Relentless. Be Unstoppable. Be YOU! 🔥',
  'Not Finished Until You Win! 🎯',
  'You\'re One Sale Away from Your Best Week Yet 💎',
  'This Week: Set the Pace. Own the Board! ⚡',
];
const MSG_PRESETS = [
  'Every student you enroll is a life you change. Keep pushing — your impact is real and your rewards are waiting.',
  'The top performers aren\'t the most talented. They\'re the ones who refuse to quit. Keep going!',
  'This week is YOUR week. Set the pace. Own the board. Show everyone what you\'re made of.',
  'Remember why you started. That fire inside you — let it roar this week. You\'ve got this! 🦁',
  'Your discipline this week will be your trophy next week. Stay locked in.',
];
const QUOTE_PRESETS = [
  { quote:'The secret of getting ahead is getting started.', author:'Mark Twain' },
  { quote:'Don\'t watch the clock; do what it does. Keep going.', author:'Sam Levenson' },
  { quote:'Success is not final, failure is not fatal: it is the courage to continue that counts.', author:'Winston Churchill' },
  { quote:'Believe you can and you\'re halfway there.', author:'Theodore Roosevelt' },
  { quote:'Opportunities don\'t happen. You create them.', author:'Chris Grosser' },
  { quote:'Great things never come from comfort zones.', author:'Unknown' },
  { quote:'The harder you work for something, the greater you\'ll feel when you achieve it.', author:'Unknown' },
  { quote:'You don\'t have to be great to start, but you have to start to be great.', author:'Zig Ziglar' },
  { quote:'Champions keep playing until they get it right.', author:'Billie Jean King' },
  { quote:'Energy and persistence conquer all things.', author:'Benjamin Franklin' },
  { quote:'One sale at a time. One goal at a time. One day at a time.', author:'Vista Tech' },
  { quote:'Act as if what you do makes a difference. It does.', author:'William James' },
];

/* ── Live Preview ────────────────────────────────────────────────────────── */
const Preview: React.FC<{ g: SalesGoalData; name: string }> = ({ g, name }) => {
  const theme = THEMES.find(t => t.color === g.themeColor) || THEMES[0];
  const raf = useRef<number>(0);
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    let t = 0;
    const loop = () => { t++; setPulse(Math.sin(t / 45) * 0.5 + 0.5); raf.current = requestAnimationFrame(loop); };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, []);

  return (
    <div style={{ borderRadius:22, overflow:'hidden', boxShadow:'0 16px 48px rgba(0,0,0,.25)', background:theme.grad, position:'relative' }}>
      <div style={{ position:'absolute', width:240, height:240, borderRadius:'50%', background:'rgba(255,255,255,.07)', top:-70, right:-70, transform:`scale(${0.9+pulse*.12})`, pointerEvents:'none' }} />
      <div style={{ position:'absolute', width:120, height:120, borderRadius:'50%', background:'rgba(255,255,255,.06)', bottom:-40, left:-30, transform:`scale(${0.85+pulse*.15})`, pointerEvents:'none' }} />
      <div style={{ position:'relative', padding:'22px 18px 18px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
          <span style={{ fontSize:36, lineHeight:1, filter:'drop-shadow(0 2px 8px rgba(0,0,0,.3))' }}>{g.emoji}</span>
          {g.badge && <span style={{ background:'rgba(255,255,255,.2)', color:'#fff', fontSize:10, fontWeight:800, padding:'4px 11px', borderRadius:99, border:'1px solid rgba(255,255,255,.3)', backdropFilter:'blur(6px)' }}>{g.badge}</span>}
        </div>
        <p style={{ fontSize:10, color:'rgba(255,255,255,.7)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:5 }}>Hey {name} 👋</p>
        <h3 style={{ fontSize:20, fontWeight:900, color:'#fff', lineHeight:1.2, letterSpacing:'-.02em', marginBottom:10, textShadow:'0 2px 10px rgba(0,0,0,.25)' }}>
          {g.headline || 'Your headline appears here…'}
        </h3>
        {g.message && <p style={{ fontSize:12, color:'rgba(255,255,255,.8)', lineHeight:1.6, marginBottom:14 }}>{g.message.slice(0,120)}{g.message.length>120?'…':''}</p>}
        {g.weeklyTarget > 0 && (
          <div style={{ background:'rgba(0,0,0,.2)', borderRadius:14, padding:'12px 14px', border:'1px solid rgba(255,255,255,.15)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:7 }}>
              <p style={{ fontSize:10, color:'rgba(255,255,255,.75)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em' }}>🎯 Weekly Target</p>
              <p style={{ fontSize:20, fontWeight:900, color:'#fff' }}>{g.weeklyTarget} sales</p>
            </div>
            <div style={{ height:7, borderRadius:99, background:'rgba(255,255,255,.2)', overflow:'hidden' }}>
              <div style={{ height:'100%', width:'40%', borderRadius:99, background:'rgba(255,255,255,.85)' }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Main ────────────────────────────────────────────────────────────────── */
type AdminTab = 'goals' | 'quotes' | 'monitor';

const SalesGoals: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const navigate = useNavigate();
  const WEEKS = weekOptions();

  const [adminTab, setAdminTab] = useState<AdminTab>('goals');
  const [people,   setPeople]  = useState<SalesPersonInfo[]>([]);
  const [goals,    setGoals]   = useState<SalesGoalData[]>([]);
  const [quotes,   setQuotes]  = useState<SalesQuoteData[]>([]);
  const [monitor,  setMonitor] = useState<GoalMonitorEntry[]>([]);
  const [monitorInfo, setMonitorInfo] = useState<GoalMonitorWeek | null>(null);
  const [monitorBusy, setMonitorBusy] = useState(false);
  // The Monday of the week the monitor shows (undefined = this week); a ref so reloads keep it
  const monitorWeekRef = useRef<string | undefined>(undefined);
  const [loading,  setLoad]    = useState(true);
  const [saving,   setSaving]  = useState(false);
  const [saved,    setSaved]   = useState(false);

  const [selSp,    setSelSp]   = useState<string | null>(null);
  const [selWeek,  setSelWeek] = useState(WEEKS[1].key); // default = this week

  const DEFAULT_TIERS: TierThresholds = { bronze: 5, silver: 7, gold: 10, diamond: 15, diplomat: 18 };
  const blank = (): SalesGoalData => ({
    salesPersonId: null, weekKey: selWeek, weeklyTarget: 10,
    headline: '', message: '', badge: '', emoji: '🎯', themeColor: '#6d28d9',
    challenge: '', reward: '', difficulty: 'Standard',
    tierThresholds: { ...DEFAULT_TIERS },
  });
  const [form, setForm] = useState<SalesGoalData>(blank());

  // Quote form
  const [qText,   setQText]   = useState('');
  const [qAuthor, setQAuthor] = useState('');
  const [qSaving, setQSaving] = useState(false);

  const load = useCallback(async () => {
    setLoad(true);
    // Run independently so a missing model on server doesn't block SP list
    const [gr, sr, qr, mr] = await Promise.allSettled([
      getSalesGoals(adminPassword),
      getSalespeople(adminPassword),
      getSalesQuotes(adminPassword),
      getGoalMonitor(adminPassword, monitorWeekRef.current),
    ]);
    if (gr.status === 'fulfilled') setGoals(gr.value.goals);
    else toast.error('Goals: ' + (gr.reason?.message || 'failed'));
    if (sr.status === 'fulfilled') setPeople(sr.value.salespeople.filter(s => s.status === 'Active'));
    else toast.error('Salespeople: ' + (sr.reason?.message || 'failed'));
    if (qr.status === 'fulfilled') setQuotes(qr.value.quotes);
    if (mr.status === 'fulfilled') { setMonitor(mr.value.monitor); setMonitorInfo(mr.value); }
    setLoad(false);
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  // Step the monitor to another week — earlier weeks show how each goal ended
  const showMonitorWeek = async (week: string) => {
    monitorWeekRef.current = week;
    setMonitorBusy(true);
    try {
      const r = await getGoalMonitor(adminPassword, week);
      if (monitorWeekRef.current !== week) return; // a later click won
      setMonitor(r.monitor); setMonitorInfo(r);
    } catch (e: any) { toast.error(e.message); }
    finally { if (monitorWeekRef.current === week) setMonitorBusy(false); }
  };
  const monStatus = monitorInfo?.status ?? 'current';

  // Sync form when SP/week selection changes
  useEffect(() => {
    const existing = goals.find(g =>
      (selSp === null ? g.salesPersonId === null : g.salesPersonId === selSp) &&
      g.weekKey === selWeek
    );
    setForm(existing ? { ...existing } : { ...blank(), salesPersonId: selSp, weekKey: selWeek });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selSp, selWeek, goals]);

  const handleSave = async () => {
    if (!form.headline.trim()) { toast.error('Headline is required'); return; }
    setSaving(true);
    try {
      await setSalesGoal(adminPassword, { ...form, weekKey: selWeek, salesPersonId: selSp });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
      toast.success('Goal saved ✓');
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDeleteGoal = async (spId: string | null, wk: string) => {
    if (!window.confirm(`Delete goal for ${spId ? people.find(p=>p.salesPersonId===spId)?.name||spId : 'All SPs'} — ${wk}?`)) return;
    try {
      await deleteSalesGoal(adminPassword, spId, wk);
      toast.success('Goal deleted');
      load();
    } catch (e: any) { toast.error(e.message); }
  };

  const handleAddQuote = async (q?: typeof QUOTE_PRESETS[0]) => {
    const text   = q?.quote  ?? qText.trim();
    const author = q?.author ?? qAuthor.trim();
    if (!text) { toast.error('Quote text required'); return; }
    setQSaving(true);
    try {
      await addSalesQuote(adminPassword, text, author);
      setQText(''); setQAuthor('');
      toast.success('Quote added ✓');
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setQSaving(false); }
  };

  const handleDeleteQuote = async (id: string) => {
    try { await deleteSalesQuote(adminPassword, id); toast.success('Deleted'); load(); }
    catch (e: any) { toast.error(e.message); }
  };

  const handleToggleQuote = async (id: string) => {
    try { await toggleSalesQuote(adminPassword, id); load(); }
    catch (e: any) { toast.error(e.message); }
  };

  const previewName = selSp
    ? (people.find(p => p.salesPersonId === selSp)?.name.split(' ')[0] || 'SP')
    : 'Team';

  const todayIdx = Math.floor((Date.now() - new Date(new Date().getFullYear(),0,0).getTime()) / 86400000);
  const activeQuotes = quotes.filter(q => q.active);
  const todayQuote = activeQuotes.length > 0
    ? activeQuotes[todayIdx % activeQuotes.length]
    : null;

  const CSS = `
    @keyframes fadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }
    @keyframes gradShift{0%{background-position:0%50%}50%{background-position:100%50%}100%{background-position:0%50%}}
    .fu1{animation:fadeUp .3s ease both}
    .fu2{animation:fadeUp .3s .07s ease both}
    .fu3{animation:fadeUp .3s .14s ease both}
    .atab{display:flex;align-items:center;gap:7px;padding:10px 18px;border-radius:12px;border:none;
      font-size:13px;font-weight:700;cursor:pointer;transition:all .15s;font-family:inherit}
    .sp-card{cursor:pointer;transition:all .15s;border:2px solid transparent;border-radius:14px}
    .sp-card:hover{transform:translateY(-1px);box-shadow:0 6px 18px rgba(0,0,0,.1)}
    .sp-card.active{border-color:#6d28d9!important}
    .week-btn{padding:7px 14px;border-radius:99px;border:1.5px solid #e2e8f0;background:#fff;
      font-size:11px;font-weight:700;cursor:pointer;transition:all .12s;font-family:inherit;white-space:nowrap}
    .week-btn.active{background:#6d28d9;color:#fff;border-color:#6d28d9;box-shadow:0 4px 12px rgba(109,40,217,.35)}
    .week-btn:hover:not(.active):not(:disabled){background:#f5f3ff;border-color:#8b5cf6}
    .week-btn:disabled{opacity:.5;cursor:default}
    .theme-dot{width:30px;height:30px;border-radius:50%;cursor:pointer;transition:all .15s;border:3px solid transparent;flex-shrink:0}
    .theme-dot:hover{transform:scale(1.12)}
    .theme-dot.active{border-color:#0f172a;transform:scale(1.18)}
    .emoji-btn{cursor:pointer;font-size:20px;border:none;background:transparent;padding:5px;border-radius:9px;transition:all .12s;line-height:1}
    .emoji-btn:hover{background:#f5f3ff;transform:scale(1.15)}
    .emoji-btn.active{background:#ede9fe;box-shadow:0 0 0 2px #7c3aed}
    .preset-btn{cursor:pointer;padding:5px 10px;border-radius:99px;font-size:11px;font-weight:700;border:1.5px solid #e2e8f0;background:#fff;transition:all .12s;font-family:inherit;line-height:1.3}
    .preset-btn:hover{background:#f5f3ff;border-color:#8b5cf6;color:#5b21b6}
    .quote-card{transition:all .15s;border-radius:14px;border:1.5px solid #e8ecf6;background:#fff;margin-bottom:8px}
    .quote-card:hover{box-shadow:0 4px 14px rgba(0,0,0,.08)}
    .save-glow{box-shadow:0 0 0 0 rgba(109,40,217,.4);animation:saveGlow 2s infinite}
    @keyframes saveGlow{0%,100%{box-shadow:0 8px 24px rgba(109,40,217,.35)}50%{box-shadow:0 8px 32px rgba(109,40,217,.55)}}
  `;

  if (loading) return (
    <TrackerLayout>
      <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:300 }}>
        <div className="animate-spin" style={{ width:36, height:36, borderRadius:'50%', border:'3px solid #e5e7eb', borderTopColor:'#6d28d9' }} />
      </div>
    </TrackerLayout>
  );

  return (
    <TrackerLayout>
      <style>{CSS}</style>
      <div style={{ maxWidth:1200, margin:'0 auto', padding:'0 0 80px', fontFamily:'"Plus Jakarta Sans",Inter,system-ui,sans-serif' }}>

        {/* ── Grand Header ── */}
        <div className="fu1" style={{
          borderRadius:24, padding:'26px 24px', marginBottom:22, position:'relative', overflow:'hidden',
          background:'linear-gradient(135deg,#1e1b4b 0%,#4c1d95 40%,#6d28d9 70%,#a21caf 100%)',
          backgroundSize:'200% 200%', animation:'gradShift 5s ease infinite',
          boxShadow:'0 16px 48px rgba(109,40,217,.45)',
        }}>
          <div style={{ position:'absolute', width:260, height:260, borderRadius:'50%', background:'rgba(255,255,255,.05)', top:-80, right:-60, pointerEvents:'none' }} />
          <div style={{ position:'absolute', width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,.05)', bottom:-50, left:20, pointerEvents:'none' }} />
          <div style={{ position:'relative', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <button onClick={() => navigate(-1)} style={{ width:34, height:34, borderRadius:10, background:'rgba(255,255,255,.15)', border:'none', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <ChevronLeft size={16} />
              </button>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:3 }}>
                  <Target size={16} color="#c4b5fd" />
                  <p style={{ fontSize:11, color:'#c4b5fd', fontWeight:700, textTransform:'uppercase', letterSpacing:'.09em' }}>Admin · Motivation Engine</p>
                </div>
                <h1 style={{ fontSize:24, fontWeight:900, color:'#fff', letterSpacing:'-.02em', lineHeight:1 }}>
                  Fire Up Your Sales Team 🔥
                </h1>
                <p style={{ fontSize:12, color:'rgba(255,255,255,.7)', marginTop:5 }}>
                  Set weekly goals · Manage daily quote rotation · Watch your team soar
                </p>
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={load} title="Refresh" style={{ width:36, height:36, borderRadius:10, background:'rgba(255,255,255,.15)', border:'none', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <RefreshCw size={14} />
              </button>
            </div>
          </div>

          {/* Stats bar */}
          <div style={{ display:'flex', gap:12, marginTop:18, flexWrap:'wrap' }}>
            {[
              { emoji:'📅', label:'Goals set', val: goals.length },
              { emoji:'💬', label:'Quotes in pool', val: quotes.length },
              { emoji:'✅', label:'Active quotes', val: activeQuotes.length },
              { emoji:'👥', label:'Salespeople', val: people.length },
            ].map(s => (
              <div key={s.label} style={{ background:'rgba(255,255,255,.12)', backdropFilter:'blur(8px)', borderRadius:12, padding:'8px 14px', border:'1px solid rgba(255,255,255,.2)' }}>
                <p style={{ fontSize:16, fontWeight:900, color:'#fff' }}>{s.emoji} {s.val}</p>
                <p style={{ fontSize:9, color:'rgba(255,255,255,.65)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', marginTop:2 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Admin Tabs ── */}
        <div className="fu2" style={{ display:'flex', gap:8, marginBottom:20 }}>
          {([
            { id:'goals'   as AdminTab, label:'Weekly Goals',           icon:<Target size={14}/> },
            { id:'monitor' as AdminTab, label:`Live Monitor (${people.length})`, icon:<Zap size={14}/> },
            { id:'quotes'  as AdminTab, label:`Daily Quotes (${quotes.length})`, icon:<Quote size={14}/> },
          ]).map(t => (
            <button key={t.id} className="atab"
              onClick={() => setAdminTab(t.id)}
              style={{
                background: adminTab===t.id ? '#6d28d9' : '#fff',
                color:       adminTab===t.id ? '#fff' : '#64748b',
                boxShadow:   adminTab===t.id ? '0 4px 14px rgba(109,40,217,.35)' : '0 1px 4px rgba(0,0,0,.07)',
              }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ══════════ GOALS TAB ══════════ */}
        {adminTab === 'goals' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, alignItems:'start' }}>

            {/* Left: editor */}
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

              {/* Week selector */}
              <div style={{ background:'#fff', borderRadius:18, padding:'18px', boxShadow:'0 2px 10px rgba(0,0,0,.07)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:12 }}>
                  <Calendar size={14} color="#6d28d9" />
                  <p style={{ fontSize:12, fontWeight:800, color:'#0f172a' }}>Which Week?</p>
                </div>
                <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:4 }}>
                  {WEEKS.map(w => (
                    <button key={w.key} className={`week-btn ${selWeek===w.key?'active':''}`}
                      onClick={() => setSelWeek(w.key)}>
                      {w.isNow ? '⚡ This Week' : w.label}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize:11, color:'#94a3b8', marginTop:8, fontWeight:600 }}>
                  Goal for: <strong style={{ color:'#6d28d9' }}>{labelForKey(selWeek)}</strong>
                </p>
              </div>

              {/* SP selector */}
              <div style={{ background:'#fff', borderRadius:18, padding:'18px', boxShadow:'0 2px 10px rgba(0,0,0,.07)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:12 }}>
                  <Users size={14} color="#6d28d9" />
                  <p style={{ fontSize:12, fontWeight:800, color:'#0f172a' }}>Who gets this goal?</p>
                </div>
                {/* Global */}
                <div className={`sp-card ${selSp===null?'active':''}`}
                  onClick={() => setSelSp(null)}
                  style={{ background:selSp===null?'#f5f3ff':'#fafafa', padding:'10px 12px', marginBottom:8, display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#6d28d9,#a855f7)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Globe size={16} color="#fff" />
                  </div>
                  <div style={{ flex:1 }}>
                    <p style={{ fontSize:13, fontWeight:800, color:'#0f172a' }}>🌐 All Salespeople</p>
                    <p style={{ fontSize:10, color:'#94a3b8' }}>Global — fallback for everyone</p>
                  </div>
                  {selSp===null && <CheckCircle2 size={16} color="#6d28d9" />}
                </div>
                {people.map(sp => {
                  const hasGoal = goals.some(g => g.salesPersonId===sp.salesPersonId && g.weekKey===selWeek);
                  return (
                    <div key={sp.salesPersonId}
                      className={`sp-card ${selSp===sp.salesPersonId?'active':''}`}
                      onClick={() => setSelSp(sp.salesPersonId)}
                      style={{ background:selSp===sp.salesPersonId?'#f5f3ff':'transparent', padding:'9px 12px', display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:34, height:34, borderRadius:'50%', flexShrink:0,
                        background:`hsl(${sp.name.charCodeAt(0)*7%360},60%,55%)`,
                        display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:900, color:'#fff' }}>
                        {sp.name.charAt(0)}
                      </div>
                      <div style={{ flex:1 }}>
                        <p style={{ fontSize:13, fontWeight:700, color:'#0f172a' }}>{sp.name}</p>
                        <p style={{ fontSize:10, color:'#94a3b8' }}>{sp.salesPersonId}</p>
                      </div>
                      {hasGoal && <span style={{ fontSize:9, fontWeight:800, background:'#dcfce7', color:'#15803d', padding:'2px 7px', borderRadius:99, border:'1px solid #86efac' }}>SET</span>}
                      {selSp===sp.salesPersonId && <CheckCircle2 size={15} color="#6d28d9" />}
                    </div>
                  );
                })}
              </div>

              {/* Goal form */}
              <div style={{ background:'#fff', borderRadius:18, padding:'18px', boxShadow:'0 2px 10px rgba(0,0,0,.07)' }}>
                {/* Headline */}
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.07em', display:'block', marginBottom:7 }}>
                    💥 Big Headline
                  </label>
                  <textarea rows={2} value={form.headline}
                    onChange={e => setForm(f=>({...f, headline:e.target.value}))}
                    placeholder="Write something bold and inspiring…"
                    style={{ width:'100%', borderRadius:10, border:'1.5px solid #e2e8f0', padding:'9px 11px', fontSize:13, fontWeight:700, resize:'none', outline:'none', boxSizing:'border-box', fontFamily:'inherit', color:'#0f172a', transition:'border-color .15s' }}
                    onFocus={e=>e.target.style.borderColor='#6d28d9'}
                    onBlur={e=>e.target.style.borderColor='#e2e8f0'} />
                  <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:6 }}>
                    {HEADLINE_PRESETS.map(p => (
                      <button key={p} className="preset-btn" onClick={() => setForm(f=>({...f,headline:p}))}>
                        {p.length>30?p.slice(0,30)+'…':p}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.07em', display:'block', marginBottom:7 }}>
                    📝 Message
                  </label>
                  <textarea rows={3} value={form.message}
                    onChange={e => setForm(f=>({...f, message:e.target.value}))}
                    placeholder="Write a personal, energetic message…"
                    style={{ width:'100%', borderRadius:10, border:'1.5px solid #e2e8f0', padding:'9px 11px', fontSize:12, resize:'none', outline:'none', boxSizing:'border-box', fontFamily:'inherit', color:'#334155', transition:'border-color .15s' }}
                    onFocus={e=>e.target.style.borderColor='#6d28d9'}
                    onBlur={e=>e.target.style.borderColor='#e2e8f0'} />
                  <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginTop:6 }}>
                    {MSG_PRESETS.map((p,i) => (
                      <button key={i} className="preset-btn" onClick={() => setForm(f=>({...f,message:p}))}>Preset {i+1}</button>
                    ))}
                  </div>
                </div>

                {/* Badge Picker */}
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:8 }}>🏅 Goal Badge — Select or type custom</label>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:8 }}>
                    {BADGE_PRESETS.map(b => (
                      <button key={b.label} onClick={() => setForm(f=>({...f, badge: form.badge===b.label?'':b.label}))}
                        style={{ padding:'5px 10px', borderRadius:99, fontSize:11, fontWeight:800, cursor:'pointer', fontFamily:'inherit', transition:'all .12s',
                          border:`1.5px solid ${form.badge===b.label?b.color:b.color+'50'}`,
                          background: form.badge===b.label?b.bg:'#fafafa',
                          color: form.badge===b.label?b.color:'#64748b',
                          boxShadow: form.badge===b.label?`0 0 0 2px ${b.color}40`:'none',
                        }}>
                        {b.label}
                      </button>
                    ))}
                  </div>
                  <input value={form.badge} onChange={e=>setForm(f=>({...f,badge:e.target.value}))} placeholder="Or type a custom badge…"
                    style={{ width:'100%', borderRadius:9, border:'1.5px solid #e2e8f0', padding:'7px 10px', fontSize:12, fontWeight:700, outline:'none', boxSizing:'border-box', fontFamily:'inherit', transition:'border-color .15s', color:'#0f172a' }}
                    onFocus={e=>e.target.style.borderColor='#6d28d9'} onBlur={e=>e.target.style.borderColor='#e2e8f0'} />
                </div>

                {/* Target */}
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:6 }}>🎯 Weekly Target (sales)</label>
                  <input type="number" min={0} max={100} value={form.weeklyTarget}
                    onChange={e=>setForm(f=>({...f,weeklyTarget:+e.target.value}))}
                    style={{ width:'100%', borderRadius:9, border:'1.5px solid #e2e8f0', padding:'8px 10px', fontSize:16, fontWeight:900, outline:'none', boxSizing:'border-box', fontFamily:'inherit', transition:'border-color .15s', color:'#6d28d9' }}
                    onFocus={e=>e.target.style.borderColor='#6d28d9'} onBlur={e=>e.target.style.borderColor='#e2e8f0'} />
                </div>

                {/* Tier Thresholds */}
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.06em', display:'flex', alignItems:'center', gap:5, marginBottom:9 }}>
                    <Layers size={12} /> Tier Thresholds (sales needed)
                  </label>
                  <div style={{ background:'#f8fafc', borderRadius:10, padding:'10px 12px', border:'1px solid #e2e8f0' }}>
                    <p style={{ fontSize:10, color:'#94a3b8', fontWeight:600, marginBottom:8 }}>
                      Customize how many sales each tier requires for {selSp ? previewName : 'all SPs'}. Defaults: Bronze=5, Silver=7, Gold=10, Diamond=15, Diplomat=18
                    </p>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:6 }}>
                      {([
                        { key:'bronze',   label:'🥉 Bronze',   color:'#d97706' },
                        { key:'silver',   label:'🥈 Silver',   color:'#64748b' },
                        { key:'gold',     label:'🥇 Gold',     color:'#f59e0b' },
                        { key:'diamond',  label:'💎 Diamond',  color:'#0ea5e9' },
                        { key:'diplomat', label:'👑 Diplomat', color:'#7c3aed' },
                      ] as { key: keyof TierThresholds; label: string; color: string }[]).map(t => (
                        <div key={t.key} style={{ textAlign:'center' }}>
                          <label style={{ fontSize:9, fontWeight:800, color:t.color, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:4 }}>{t.label}</label>
                          <input
                            type="number" min={1} max={50}
                            value={form.tierThresholds?.[t.key] ?? DEFAULT_TIERS[t.key]}
                            onChange={e => setForm(f => ({
                              ...f,
                              tierThresholds: { ...DEFAULT_TIERS, ...f.tierThresholds, [t.key]: +e.target.value }
                            }))}
                            style={{ width:'100%', borderRadius:7, border:`1.5px solid ${t.color}40`, padding:'5px 4px', fontSize:13, fontWeight:800, textAlign:'center', outline:'none', boxSizing:'border-box', fontFamily:'inherit', color:t.color, background:'#fff' }}
                          />
                        </div>
                      ))}
                    </div>
                    <button onClick={() => setForm(f=>({...f, tierThresholds:{...DEFAULT_TIERS}}))}
                      style={{ marginTop:8, fontSize:10, fontWeight:700, color:'#94a3b8', background:'transparent', border:'none', cursor:'pointer', fontFamily:'inherit' }}>
                      ↺ Reset to defaults
                    </button>
                  </div>
                </div>

                {/* Emoji */}
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:7 }}>✨ Emoji</label>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                    {EMOJIS.map(e => <button key={e} className={`emoji-btn ${form.emoji===e?'active':''}`} onClick={()=>setForm(f=>({...f,emoji:e}))}>{e}</button>)}
                  </div>
                </div>

                {/* Theme */}
                <div style={{ marginBottom:20 }}>
                  <label style={{ fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.06em', display:'flex', alignItems:'center', gap:5, marginBottom:9 }}>
                    <Palette size={12} /> Theme
                  </label>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
                    {THEMES.map(t => (
                      <div key={t.color} className={`theme-dot ${form.themeColor===t.color?'active':''}`}
                        title={t.label} onClick={()=>setForm(f=>({...f,themeColor:t.color}))}
                        style={{ background:t.grad }} />
                    ))}
                  </div>
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                    {THEMES.map(t => (
                      <button key={t.color} onClick={()=>setForm(f=>({...f,themeColor:t.color}))}
                        style={{ fontSize:10, fontWeight:700, padding:'3px 9px', borderRadius:99,
                          border:`1.5px solid ${form.themeColor===t.color?t.color:'#e2e8f0'}`,
                          background:form.themeColor===t.color?t.color:'#fff',
                          color:form.themeColor===t.color?'#fff':'#64748b', cursor:'pointer', transition:'all .12s' }}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Save */}
                <button onClick={handleSave} disabled={saving}
                  className={!saving&&!saved?'save-glow':''}
                  style={{ width:'100%', padding:'13px', borderRadius:12, border:'none', cursor:'pointer',
                    fontSize:14, fontWeight:900, fontFamily:'inherit',
                    background:saved?'linear-gradient(135deg,#16a34a,#4ade80)':'linear-gradient(135deg,#6d28d9,#a855f7)',
                    color:'#fff', transition:'all .2s',
                    display:'flex', alignItems:'center', justifyContent:'center', gap:7 }}>
                  {saving ? <><div className="animate-spin" style={{ width:16,height:16,borderRadius:'50%',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff' }} /> Saving…</>
                    : saved ? <><CheckCircle2 size={16} /> Saved!</>
                    : <><Save size={14} /> Save for {selSp ? previewName : 'All SPs'} · {WEEKS.find(w=>w.key===selWeek)?.isNow?'This Week':labelForKey(selWeek)}</>}
                </button>
              </div>

              {/* Goals this week summary */}
              {goals.filter(g=>g.weekKey===selWeek).length > 0 && (
                <div style={{ background:'#fff', borderRadius:18, padding:'16px', boxShadow:'0 2px 8px rgba(0,0,0,.06)' }}>
                  <p style={{ fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:10 }}>
                    Goals for {labelForKey(selWeek)}
                  </p>
                  {goals.filter(g=>g.weekKey===selWeek).map(g => {
                    const spName = g.salesPersonId
                      ? (people.find(p=>p.salesPersonId===g.salesPersonId)?.name||g.salesPersonId)
                      : '🌐 Global';
                    const th = THEMES.find(t=>t.color===g.themeColor)||THEMES[0];
                    return (
                      <div key={g.salesPersonId??'global'}
                        style={{ display:'flex', alignItems:'center', gap:9, padding:'9px 0', borderBottom:'1px solid #f1f5f9' }}>
                        <div onClick={()=>setSelSp(g.salesPersonId)} style={{ width:28, height:28, borderRadius:8, background:th.grad, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, cursor:'pointer' }}>{g.emoji}</div>
                        <div onClick={()=>setSelSp(g.salesPersonId)} style={{ flex:1, minWidth:0, cursor:'pointer' }}>
                          <p style={{ fontSize:12, fontWeight:800, color:'#0f172a' }}>{spName}</p>
                          <p style={{ fontSize:11, color:'#94a3b8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.headline||'—'}</p>
                        </div>
                        <span style={{ fontSize:11, fontWeight:800, background:'#ede9fe', color:'#6d28d9', padding:'2px 9px', borderRadius:99, flexShrink:0 }}>{g.weeklyTarget}🎯</span>
                        <button onClick={()=>handleDeleteGoal(g.salesPersonId, g.weekKey||'')}
                          style={{ width:26, height:26, borderRadius:7, border:'1px solid #fee2e2', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#dc2626', flexShrink:0 }}
                          title="Delete goal">
                          <Trash2 size={12}/>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right: live preview */}
            <div style={{ position:'sticky', top:80 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10 }}>
                <Sparkles size={13} color="#6d28d9" />
                <p style={{ fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.08em' }}>
                  Live Preview — {previewName}'s View
                </p>
              </div>
              <Preview g={form} name={previewName} />

              {/* Week navigation on preview */}
              <div style={{ marginTop:14, background:'#fff', borderRadius:16, padding:'14px', boxShadow:'0 2px 8px rgba(0,0,0,.06)' }}>
                <p style={{ fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:9 }}>All Weeks with Goals</p>
                {goals.length === 0 && <p style={{ fontSize:12, color:'#94a3b8', textAlign:'center', padding:'12px 0' }}>No goals set yet</p>}
                {Array.from(new Set(goals.map(g=>g.weekKey))).sort((a,b)=>(b||'').localeCompare(a||'')).slice(0,6).map(wk => {
                  const weekGoals = goals.filter(g=>g.weekKey===wk);
                  return (
                    <div key={wk||'global'} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 0', borderBottom:'1px solid #f1f5f9' }}>
                      <div style={{ fontSize:11, fontWeight:700, color:'#64748b', minWidth:90 }}>
                        {wk ? labelForKey(wk) : '—'}
                      </div>
                      <div style={{ display:'flex', gap:4, flex:1, flexWrap:'wrap' }}>
                        {weekGoals.map(g => {
                          const th = THEMES.find(t=>t.color===g.themeColor)||THEMES[0];
                          return (
                            <div key={g.salesPersonId??'global'} style={{ width:18, height:18, borderRadius:5, background:th.grad, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10 }} title={g.salesPersonId||'Global'}>
                              {g.emoji}
                            </div>
                          );
                        })}
                      </div>
                      <button onClick={()=>setSelWeek(wk||'')} style={{ fontSize:10, fontWeight:700, color:'#6d28d9', background:'#ede9fe', border:'none', borderRadius:6, padding:'2px 7px', cursor:'pointer' }}>
                        Edit
                      </button>
                      <button onClick={e=>{e.stopPropagation(); weekGoals.forEach(g=>handleDeleteGoal(g.salesPersonId,g.weekKey||''));}}
                        style={{ width:22, height:22, borderRadius:6, border:'1px solid #fee2e2', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#dc2626', flexShrink:0 }}
                        title="Delete all goals for this week">
                        <Trash2 size={11}/>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ══════════ MONITOR TAB ══════════ */}
        {adminTab === 'monitor' && (
          <div>
            {/* Header + week picker */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap', marginBottom:16 }}>
              <div>
                <p style={{ fontSize:13, fontWeight:800, color:'#0f172a' }}>
                  {monStatus==='past' ? '📊 Goal Results' : monStatus==='upcoming' ? '🗓️ Upcoming Goals' : '⚡ Live Goal Progress Monitor'}
                </p>
                <p style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
                  Week: <strong style={{ color:'#6d28d9' }}>{monitorInfo?.weekLabel || '—'}</strong>
                  {' · '}{monStatus==='past' ? 'Final — this week is over' : monStatus==='upcoming' ? 'Not started yet' : 'Live'} · Sorted by % achieved
                </p>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                <button className="week-btn" disabled={!monitorInfo || monitorBusy}
                  onClick={() => monitorInfo && showMonitorWeek(monitorInfo.prevWeekKey)}
                  style={{ display:'flex', alignItems:'center', gap:3 }}>
                  <ChevronLeft size={12}/> Previous week
                </button>
                {monStatus !== 'current' && monitorInfo && (
                  <button className="week-btn active" disabled={monitorBusy} onClick={() => showMonitorWeek(monitorInfo.currentWeekKey)}>
                    ⚡ This Week
                  </button>
                )}
                <button className="week-btn" disabled={!monitorInfo || monitorBusy}
                  onClick={() => monitorInfo && showMonitorWeek(monitorInfo.nextWeekKey)}
                  style={{ display:'flex', alignItems:'center', gap:3 }}>
                  Next week <ChevronRight size={12}/>
                </button>
                <button onClick={load} style={{ padding:'7px 14px', borderRadius:10, border:'none', background:'#6d28d9', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:5, fontFamily:'inherit' }}>
                  <RefreshCw size={11}/> Refresh
                </button>
              </div>
            </div>

            {monitor.length === 0 ? (
              <div style={{ textAlign:'center', padding:'60px 0', background:'#fff', borderRadius:20 }}>
                <p style={{ fontSize:40 }}>📊</p>
                <p style={{ fontSize:14, fontWeight:700, color:'#1e293b', marginTop:12 }}>No data yet</p>
                <p style={{ fontSize:12, color:'#94a3b8', marginTop:6 }}>Set goals and record sales to see live progress</p>
              </div>
            ) : (
              <>
                {/* Summary strip */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16 }}>
                  {[
                    { label:'Crushing It (≥75%)', val: monitor.filter(m=>m.pct>=75).length, color:'#16a34a', bg:'#dcfce7', emoji:'🔥' },
                    { label:'On Track (50–74%)',   val: monitor.filter(m=>m.pct>=50&&m.pct<75).length, color:'#2563eb', bg:'#dbeafe', emoji:'📈' },
                    { label:'Needs Push (<50%)',   val: monitor.filter(m=>m.pct>0&&m.pct<50).length, color:'#d97706', bg:'#fef3c7', emoji:'⚡' },
                    { label:'No Goal Set',         val: monitor.filter(m=>!m.hasGoal).length, color:'#94a3b8', bg:'#f1f5f9', emoji:'⚪' },
                  ].map(s => (
                    <div key={s.label} style={{ background:s.bg, borderRadius:14, padding:'12px 14px', border:`1.5px solid ${s.color}30` }}>
                      <p style={{ fontSize:22, fontWeight:900, color:s.color }}>{s.emoji} {s.val}</p>
                      <p style={{ fontSize:10, fontWeight:700, color:s.color, textTransform:'uppercase', letterSpacing:'.05em', marginTop:3, opacity:.8 }}>{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* SP cards grid */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14, opacity: monitorBusy ? .5 : 1, transition:'opacity .15s' }}>
                  {monitor.map((m, idx) => {
                    const th = THEMES.find(t=>t.color===m.themeColor) || THEMES[0];
                    const statusColor = m.pct>=100?'#16a34a':m.pct>=75?'#2563eb':m.pct>=50?'#f59e0b':m.pct>0?'#ef4444':'#94a3b8';
                    const statusBg    = m.pct>=100?'#dcfce7':m.pct>=75?'#dbeafe':m.pct>=50?'#fef9c3':m.pct>0?'#fee2e2':'#f1f5f9';
                    const rankMedal   = monStatus==='upcoming' ? '' : idx===0?'🥇':idx===1?'🥈':idx===2?'🥉':'';
                    return (
                      <div key={m.salesPersonId} style={{
                        borderRadius:18, overflow:'hidden', boxShadow:'0 4px 18px rgba(0,0,0,.1)',
                        border:`1.5px solid ${statusColor}30`, background:'#fff',
                        animation:`fadeUp .3s ${idx*0.04}s ease both`,
                      }}>
                        {/* Color top band */}
                        <div style={{ height:5, background:th.grad }} />
                        <div style={{ padding:'14px 16px' }}>
                          {/* Name row */}
                          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                            <div style={{
                              width:42, height:42, borderRadius:'50%', flexShrink:0,
                              background:th.grad, display:'flex', alignItems:'center', justifyContent:'center',
                              fontSize:18, fontWeight:900, color:'#fff', boxShadow:`0 4px 12px ${m.themeColor}50`,
                            }}>
                              {m.emoji}
                            </div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <p style={{ fontSize:13, fontWeight:900, color:'#0f172a' }}>
                                {rankMedal} {m.name}
                                {m.status && m.status !== 'Active' && (
                                  <span style={{ marginLeft:6, fontSize:9, fontWeight:800, background:'#f1f5f9', color:'#64748b', padding:'1px 6px', borderRadius:99, verticalAlign:'middle' }}>Inactive</span>
                                )}
                              </p>
                              {m.badge && (
                                <span style={{ fontSize:9, fontWeight:800, background:statusBg, color:statusColor, padding:'2px 7px', borderRadius:99, border:`1px solid ${statusColor}40` }}>
                                  {m.badge}
                                </span>
                              )}
                            </div>
                            <div style={{ textAlign:'right', flexShrink:0 }}>
                              <p style={{ fontSize:22, fontWeight:900, color:statusColor, lineHeight:1 }}>{m.pct}%</p>
                              <p style={{ fontSize:9, color:'#94a3b8', fontWeight:600 }}>done</p>
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div style={{ height:10, borderRadius:99, background:'#f1f5f9', overflow:'hidden', marginBottom:8 }}>
                            <div style={{
                              height:'100%', borderRadius:99,
                              background: m.pct>=100
                                ? 'linear-gradient(90deg,#16a34a,#4ade80)'
                                : m.pct>=75
                                  ? 'linear-gradient(90deg,#2563eb,#60a5fa)'
                                  : m.pct>=50
                                    ? 'linear-gradient(90deg,#f59e0b,#fcd34d)'
                                    : 'linear-gradient(90deg,#ef4444,#f87171)',
                              width:`${m.pct}%`, transition:'width 1s ease',
                              boxShadow:`0 0 8px ${statusColor}60`,
                            }} />
                          </div>

                          {/* Sales vs target */}
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            <div style={{ display:'flex', gap:10 }}>
                              <div style={{ textAlign:'center' }}>
                                <p style={{ fontSize:20, fontWeight:900, color:'#0f172a', lineHeight:1 }}>{m.sales}</p>
                                <p style={{ fontSize:9, color:'#94a3b8', fontWeight:700, textTransform:'uppercase' }}>Sales</p>
                              </div>
                              <div style={{ width:1, background:'#e2e8f0', margin:'2px 0' }} />
                              <div style={{ textAlign:'center' }}>
                                <p style={{ fontSize:20, fontWeight:900, color:'#64748b', lineHeight:1 }}>{m.target||'—'}</p>
                                <p style={{ fontSize:9, color:'#94a3b8', fontWeight:700, textTransform:'uppercase' }}>Target</p>
                              </div>
                            </div>
                            <div style={{ textAlign:'right' }}>
                              <div style={{ fontSize:10, fontWeight:800, color:statusColor, background:statusBg, padding:'3px 9px', borderRadius:99, border:`1px solid ${statusColor}30` }}>
                                {m.tier.name} {m.tier.name==='Diamond'?'💎':m.tier.name==='Diplomat'?'👑':m.tier.name==='Gold'?'🥇':m.tier.name==='Silver'?'🥈':m.tier.name==='Bronze'?'🥉':''}
                              </div>
                              {m.pct<100 && m.target>0 && monStatus!=='upcoming' && (
                                <p style={{ fontSize:10, color: monStatus==='past' ? '#dc2626' : '#94a3b8', fontWeight: monStatus==='past' ? 700 : 400, marginTop:4 }}>
                                  {monStatus==='past' ? `Missed by ${m.target-m.sales}` : `${m.target-m.sales} more to goal`}
                                </p>
                              )}
                              {m.pct>=100 && <p style={{ fontSize:10, color:'#16a34a', marginTop:4, fontWeight:700 }}>🎉 Goal Crushed!</p>}
                            </div>
                          </div>

                          {!m.hasGoal && (
                            <div style={{ marginTop:10, padding:'6px 10px', borderRadius:8, background:'#f8fafc', border:'1px dashed #e2e8f0', textAlign:'center' }}>
                              <p style={{ fontSize:10, color:'#94a3b8', fontWeight:700 }}>No goal set for this week</p>
                            </div>
                          )}
                          {m.hasGoal && m.carriedOver && (
                            <div style={{ marginTop:10, padding:'6px 10px', borderRadius:8, background:'#fffbeb', border:'1px dashed #fcd34d', textAlign:'center' }}>
                              <p style={{ fontSize:10, color:'#92400e', fontWeight:700 }}>
                                No goal set for this week — {m.goalWeekKey ? `the ${labelForKey(m.goalWeekKey)} goal carries on` : 'an earlier goal carries on'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* ══════════ QUOTES TAB ══════════ */}
        {adminTab === 'quotes' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, alignItems:'start' }}>

            {/* Left: add + list */}
            <div>
              {/* Today's quote preview */}
              {todayQuote && (
                <div style={{ background:'linear-gradient(135deg,#0f172a,#1e1b4b,#312e81)', borderRadius:18, padding:'18px', marginBottom:16, boxShadow:'0 8px 28px rgba(15,23,42,.35)' }}>
                  <p style={{ fontSize:10, color:'#818cf8', fontWeight:800, textTransform:'uppercase', letterSpacing:'.09em', marginBottom:8 }}>✨ Today's Quote ({new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'short'})})</p>
                  <p style={{ fontSize:14, fontWeight:700, color:'#e2e8f0', fontStyle:'italic', lineHeight:1.6, marginBottom:8 }}>"{todayQuote.quote}"</p>
                  {todayQuote.author && <p style={{ fontSize:12, fontWeight:800, color:'#818cf8', textAlign:'right' }}>— {todayQuote.author}</p>}
                  <div style={{ marginTop:10, height:2, background:'linear-gradient(90deg,#6366f1,#a855f7,#ec4899)' }} />
                </div>
              )}

              {/* Add quote form */}
              <div style={{ background:'#fff', borderRadius:18, padding:'18px', boxShadow:'0 2px 10px rgba(0,0,0,.07)', marginBottom:16 }}>
                <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:14 }}>
                  <Plus size={14} color="#6d28d9" />
                  <p style={{ fontSize:13, fontWeight:800, color:'#0f172a' }}>Add New Quote</p>
                </div>
                <textarea rows={3} value={qText} onChange={e=>setQText(e.target.value)}
                  placeholder="Type a motivational quote…"
                  style={{ width:'100%', borderRadius:10, border:'1.5px solid #e2e8f0', padding:'9px 11px', fontSize:13, fontStyle:'italic', resize:'none', outline:'none', boxSizing:'border-box', fontFamily:'inherit', transition:'border-color .15s' }}
                  onFocus={e=>e.target.style.borderColor='#6d28d9'} onBlur={e=>e.target.style.borderColor='#e2e8f0'} />
                <input value={qAuthor} onChange={e=>setQAuthor(e.target.value)}
                  placeholder="Author (optional)"
                  style={{ width:'100%', borderRadius:9, border:'1.5px solid #e2e8f0', padding:'8px 11px', fontSize:12, fontWeight:700, outline:'none', boxSizing:'border-box', fontFamily:'inherit', marginTop:8, transition:'border-color .15s' }}
                  onFocus={e=>e.target.style.borderColor='#6d28d9'} onBlur={e=>e.target.style.borderColor='#e2e8f0'} />
                <button onClick={() => handleAddQuote()} disabled={qSaving || !qText.trim()}
                  style={{ marginTop:10, padding:'10px 18px', borderRadius:10, border:'none', cursor:'pointer',
                    background: qText.trim() ? '#6d28d9' : '#e2e8f0',
                    color: qText.trim() ? '#fff' : '#94a3b8',
                    fontSize:13, fontWeight:700, fontFamily:'inherit',
                    display:'flex', alignItems:'center', gap:6, transition:'all .15s' }}>
                  <Plus size={14} /> Add to Pool
                </button>
              </div>

              {/* Quick-add presets */}
              <div style={{ background:'#fff', borderRadius:18, padding:'18px', boxShadow:'0 2px 10px rgba(0,0,0,.07)', marginBottom:16 }}>
                <p style={{ fontSize:12, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:12 }}>⚡ Quick Add Presets</p>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {QUOTE_PRESETS.map((p,i) => (
                    <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'9px 0', borderBottom:i<QUOTE_PRESETS.length-1?'1px solid #f8fafc':'none' }}>
                      <div style={{ flex:1 }}>
                        <p style={{ fontSize:12, color:'#334155', fontStyle:'italic', lineHeight:1.5 }}>"{p.quote}"</p>
                        {p.author && <p style={{ fontSize:10, fontWeight:700, color:'#94a3b8', marginTop:3 }}>— {p.author}</p>}
                      </div>
                      <button onClick={() => handleAddQuote(p)}
                        style={{ flexShrink:0, padding:'5px 11px', borderRadius:8, border:'1.5px solid #e2e8f0', background:'#fff', cursor:'pointer', fontSize:11, fontWeight:700, color:'#6d28d9', fontFamily:'inherit', display:'flex', alignItems:'center', gap:4, transition:'all .12s' }}
                        onMouseOver={e=>{(e.currentTarget as HTMLButtonElement).style.background='#ede9fe'}}
                        onMouseOut={e=>{(e.currentTarget as HTMLButtonElement).style.background='#fff'}}>
                        <Plus size={11}/> Add
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quote list */}
              <div style={{ background:'#fff', borderRadius:18, padding:'18px', boxShadow:'0 2px 10px rgba(0,0,0,.07)' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                  <p style={{ fontSize:12, fontWeight:800, color:'#0f172a', textTransform:'uppercase', letterSpacing:'.07em' }}>
                    Quote Pool ({quotes.length})
                  </p>
                  {activeQuotes.length > 0 && (
                    <span style={{ fontSize:10, fontWeight:700, background:'#dcfce7', color:'#15803d', padding:'3px 9px', borderRadius:99, border:'1px solid #86efac' }}>
                      {activeQuotes.length} active · rotates daily
                    </span>
                  )}
                </div>
                {quotes.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'32px 0' }}>
                    <p style={{ fontSize:32 }}>💬</p>
                    <p style={{ fontSize:13, color:'#94a3b8', marginTop:8 }}>No quotes added yet</p>
                    <p style={{ fontSize:11, color:'#cbd5e1', marginTop:4 }}>Add quotes above or use quick presets</p>
                  </div>
                ) : (
                  quotes.map((q, i) => (
                    <div key={q._id} className="quote-card"
                      style={{ padding:'12px 14px', opacity: q.active ? 1 : 0.5 }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                        <div style={{ flex:1 }}>
                          <p style={{ fontSize:12, color:'#334155', fontStyle:'italic', lineHeight:1.55 }}>
                            "{q.quote}"
                          </p>
                          {q.author && <p style={{ fontSize:10, fontWeight:700, color:'#94a3b8', marginTop:4 }}>— {q.author}</p>}
                        </div>
                        <div style={{ display:'flex', gap:5, flexShrink:0 }}>
                          {/* Today indicator */}
                          {activeQuotes.length > 0 && activeQuotes[todayIdx % activeQuotes.length]._id === q._id && (
                            <span style={{ fontSize:9, fontWeight:800, background:'#fef9c3', color:'#92400e', padding:'2px 7px', borderRadius:99, border:'1px solid #fcd34d', alignSelf:'flex-start' }}>TODAY</span>
                          )}
                          <button onClick={() => handleToggleQuote(q._id)}
                            style={{ width:28, height:28, borderRadius:8, border:'1px solid #e2e8f0', background: q.active?'#ede9fe':'#f9fafb', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color: q.active?'#6d28d9':'#94a3b8' }}>
                            {q.active ? <Eye size={13}/> : <EyeOff size={13}/>}
                          </button>
                          <button onClick={() => handleDeleteQuote(q._id)}
                            style={{ width:28, height:28, borderRadius:8, border:'1px solid #fee2e2', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#dc2626' }}>
                            <Trash2 size={13}/>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Right: how it works + rotation schedule */}
            <div style={{ position:'sticky', top:80 }}>
              {/* How it works */}
              <div style={{ background:'linear-gradient(135deg,#0f172a,#1e1b4b)', borderRadius:20, padding:'22px', marginBottom:16, boxShadow:'0 8px 28px rgba(15,23,42,.35)' }}>
                <p style={{ fontSize:12, color:'#818cf8', fontWeight:800, textTransform:'uppercase', letterSpacing:'.09em', marginBottom:14 }}>How Daily Quotes Work</p>
                {[
                  { icon:'💬', title:'Build a Pool', desc:'Add as many quotes as you want. The more, the richer the rotation.' },
                  { icon:'🔄', title:'Auto Rotates Daily', desc:'Every day, salespeople see a different quote automatically. No manual update needed.' },
                  { icon:'📅', title:'365 Days Covered', desc:'With 30 quotes, the full year is covered. Each quote repeats after the cycle.' },
                  { icon:'👁️', title:'Active / Hidden', desc:'Toggle any quote on or off without deleting it. Hidden quotes skip the rotation.' },
                ].map(s => (
                  <div key={s.title} style={{ display:'flex', gap:12, marginBottom:14 }}>
                    <span style={{ fontSize:20, lineHeight:1, flexShrink:0 }}>{s.icon}</span>
                    <div>
                      <p style={{ fontSize:12, fontWeight:800, color:'#e2e8f0', marginBottom:3 }}>{s.title}</p>
                      <p style={{ fontSize:11, color:'rgba(255,255,255,.55)', lineHeight:1.5 }}>{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Upcoming rotation */}
              {activeQuotes.length > 0 && (
                <div style={{ background:'#fff', borderRadius:20, padding:'18px', boxShadow:'0 2px 10px rgba(0,0,0,.07)' }}>
                  <p style={{ fontSize:12, fontWeight:800, color:'#0f172a', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:12 }}>📅 Upcoming Rotation (next 7 days)</p>
                  {Array.from({length:7},(_,i)=>i).map(offset => {
                    const d = new Date(); d.setDate(d.getDate()+offset);
                    const doy = Math.floor((d.getTime()-new Date(d.getFullYear(),0,0).getTime())/86400000);
                    const q = activeQuotes[doy % activeQuotes.length];
                    const isToday = offset===0;
                    return (
                      <div key={offset} style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'8px 0', borderBottom:offset<6?'1px solid #f8fafc':'none' }}>
                        <div style={{
                          minWidth:38, padding:'3px 0', borderRadius:8, textAlign:'center',
                          background: isToday?'#6d28d9':'#f8fafc',
                          color: isToday?'#fff':'#64748b',
                          flexShrink:0,
                        }}>
                          <p style={{ fontSize:9, fontWeight:800, textTransform:'uppercase' }}>{d.toLocaleDateString('en-IN',{weekday:'short'})}</p>
                          <p style={{ fontSize:14, fontWeight:900, lineHeight:1 }}>{d.getDate()}</p>
                        </div>
                        <p style={{ fontSize:11, color:isToday?'#0f172a':'#64748b', fontWeight:isToday?700:500, fontStyle:'italic', lineHeight:1.5, flex:1 }}>
                          "{q?.quote?.slice(0,60)}{(q?.quote?.length||0)>60?'…':''}"
                          {q?.author && <span style={{ fontStyle:'normal', fontWeight:600, color:'#94a3b8' }}> — {q.author}</span>}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}

              {activeQuotes.length === 0 && (
                <div style={{ background:'#fff', borderRadius:20, padding:'32px', textAlign:'center', boxShadow:'0 2px 8px rgba(0,0,0,.06)' }}>
                  <p style={{ fontSize:36 }}>💬</p>
                  <p style={{ fontSize:14, fontWeight:700, color:'#1e293b', marginTop:10 }}>No Active Quotes</p>
                  <p style={{ fontSize:12, color:'#94a3b8', marginTop:5 }}>Add quotes to the pool to start the daily rotation. Salespeople will see a new quote every morning!</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default SalesGoals;
